import type { HTMLAttributes, Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { KibWarningFillIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import RhapHoverTooltip from '~/components/common/RhapHoverTooltip'

import styles from './ChirpWarningIcon.module.scss'

export interface ChirpWarningIconProps {
  /**
   * Additional CSS class name
   */
  className?: string
  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string
  /**
   * Icon size variant
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Tooltip text to display on hover
   * If not provided, no tooltip will be shown
   */
  tooltipText?: string
}

/**
 * A reusable Chirp-styled warning icon component.
 * Uses Kib design system icons and styling tokens.
 *
 * @example
 * ```tsx
 * <ChirpWarningIcon
 *   tooltipText="This item requires attention"
 *   size="medium"
 * />
 * ```
 */
const ChirpWarningIcon = ({
  className,
  tooltipText,
  size = 'medium',
  'data-testid': dataTestId = 'chirp-warning-icon',
}: ChirpWarningIconProps) => {
  const { t } = useTranslation()

  const iconElement = (
    <KibWarningFillIcon
      aria-label={t('Common:WARNING')}
      className={styles.icon}
    />
  )

  const renderTrigger = (
    triggerProps: HTMLAttributes<HTMLSpanElement> & {
      ref: Ref<HTMLElement>
    },
  ) => (
    <span
      {...triggerProps}
      className={classNames(styles.container, styles[size], className)}
      data-testid={dataTestId}
    >
      {iconElement}
    </span>
  )

  // Only wrap with tooltip if tooltip text is provided
  if (tooltipText) {
    return (
      <RhapHoverTooltip
        content={tooltipText}
        placement="top"
        trigger={renderTrigger}
      />
    )
  }

  return (
    <span
      className={classNames(styles.container, styles[size], className)}
      data-testid={dataTestId}
    >
      {iconElement}
    </span>
  )
}

export default ChirpWarningIcon
